import { RELATIONSHIP_CREATOR_CONSTANTS, TEMPORAL_SCOPE } from '../constants';
import type { DraftRelationshipT, RelationshipLabelDefinitionT, SelectOptionT, TemporalScopeT } from '../types/relationship';
const toTitleCase = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
type FrameRangeErrors = {
  startError: string | undefined;
  endError: string | undefined;
};
type FrameLimitT = {
  startFrameIndex: number;
  endFrameIndex: number;
};
const isTargetWithinAnySourceLimit = (frame: number, frameLimits: FrameLimitT[]): boolean => !frameLimits.length || frameLimits.some(l => frame >= l.startFrameIndex && frame <= l.endFrameIndex);
const formatLimitWindows = (limits: FrameLimitT[]): string => limits.map(l => `(${l.startFrameIndex + 1}–${l.endFrameIndex + 1})`).join(', ');
export const validateFrameRange = (startFrame: number | undefined, endFrame: number | undefined, isInterval: boolean, frameLimits: FrameLimitT[], targetAnnotationFrameRanges: FrameLimitT[] = []): FrameRangeErrors => {
  const startOutOfRange = startFrame !== undefined && !isTargetWithinAnySourceLimit(startFrame, frameLimits);
  const endOutOfRange = isInterval && endFrame !== undefined && !isTargetWithinAnySourceLimit(endFrame, frameLimits);
  const endBeforeStart = isInterval && startFrame !== undefined && endFrame !== undefined && endFrame < startFrame;
  if (startOutOfRange && endOutOfRange) {
    const sourcePart = formatLimitWindows(frameLimits);
    const targetPart = formatLimitWindows(targetAnnotationFrameRanges);
    const msg = sourcePart && targetPart ? `Source ${sourcePart} and target ${targetPart} annotations do not overlap` : RELATIONSHIP_CREATOR_CONSTANTS.VALIDATION_NO_OVERLAP;
    return {
      startError: msg,
      endError: msg
    };
  }
  const limitRange = frameLimits.length ? ` ${formatLimitWindows(frameLimits)}` : '';
  return {
    startError: startOutOfRange ? `${RELATIONSHIP_CREATOR_CONSTANTS.VALIDATION_START_FRAME_OUT_OF_RANGE}${limitRange}` : undefined,
    endError: endOutOfRange ? `${RELATIONSHIP_CREATOR_CONSTANTS.VALIDATION_END_FRAME_OUT_OF_RANGE}${limitRange}` : endBeforeStart ? RELATIONSHIP_CREATOR_CONSTANTS.VALIDATION_END_BEFORE_START : undefined
  };
};
export const buildTypeOptions = (relationshipLabels: RelationshipLabelDefinitionT[], showTemporalInfo?: boolean): SelectOptionT[] => {
  const seen = new Set<string>();
  return relationshipLabels.reduce<SelectOptionT[]>((acc, label) => {
    const displayLabel = showTemporalInfo && label.temporalConstraints?.temporalScope ? `${label.relationshipLabelName} (${toTitleCase(label.temporalConstraints.temporalScope)})` : label.relationshipLabelName;
    if (!seen.has(displayLabel)) {
      seen.add(displayLabel);
      acc.push({
        id: label.name,
        label: displayLabel
      });
    }
    return acc;
  }, []);
};
export const resolveFrameRanges = (draft: DraftRelationshipT, sameInterval: boolean, sharedFrameRange: {
  startFrame?: number;
  endFrame?: number;
}, temporalScope?: TemporalScopeT): Array<{
  startFrameIndex: number;
  endFrameIndex: number;
}> => {
  if (!temporalScope) return [];
  if (sameInterval) {
    const {
      startFrame,
      endFrame
    } = sharedFrameRange;
    if (startFrame === undefined) return [];
    if (temporalScope === TEMPORAL_SCOPE.INSTANT) {
      return [{
        startFrameIndex: startFrame - 1,
        endFrameIndex: startFrame - 1
      }];
    }
    if (endFrame === undefined) return [];
    return [{
      startFrameIndex: startFrame - 1,
      endFrameIndex: endFrame - 1
    }];
  }
  return draft.frameRanges.filter(r => {
    if (r.startFrame === undefined) return false;
    if (temporalScope === TEMPORAL_SCOPE.INTERVAL && r.endFrame === undefined) return false;
    return true;
  }).map(r => {
    const start = r.startFrame!;
    if (temporalScope === TEMPORAL_SCOPE.INSTANT) {
      return {
        startFrameIndex: start - 1,
        endFrameIndex: start - 1
      };
    }
    return {
      startFrameIndex: start - 1,
      endFrameIndex: r.endFrame! - 1
    };
  });
};